import React, { useEffect, useState, useRef } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { showManagedToast } from "@/util/toastManager";

import Loading from "@/components/Loading";
import { getNuvidioToken } from "@/hooks/getNuvidioToken";

const ENV = (window as any).ENV || {};
const { VITE_NUVIDIO } = ENV;

if (!VITE_NUVIDIO) {
    const errorMessage = "[ENV] Variável VITE_NUVIDIO do env-config.js não foi carregada.";
    console.error(errorMessage, ENV);
    throw new Error(errorMessage);
}

/* ---------------- Styled Components ---------------- */

const PageWrapper = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; /* <--- MANTIDO: Centraliza o card verticalmente */
  background-color: var(--gray-50);
  position: relative;
`;

const LogoImage = styled.img`
  height: 2rem;
`;

const NuvidioIframe = styled.iframe`
  border: none;
  width: 100%; /* Iframe ocupa 100% da largura e altura do seu PARENT (IframeContentWrapper) */
  height: 100%;
`;

const ErrorMessage = styled.div`
  text-align: center;
  font-size: var(--fs-body);
  color: var(--error-500);
  padding: 2rem;
`;

const LogoutButton = styled.button`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 10;
  padding: 0.5rem;
  border-radius: 9999px;
  background: transparent;
  border: none;
  cursor: pointer;
  &:hover {
    background-color: var(--gray-200);
  }
  transition: background-color 0.15s ease-in-out;
`;

// Ajuste a margem vertical do card para um valor menor, mais alinhado à imagem de referência
const CARD_VERTICAL_PAGE_MARGIN = "1rem"; // <--- ALTERADO AQUI: Margem de 1rem (16px) do topo e da base da tela

const CardContainerDiv = styled.div`
  width: 90%; /* Card ocupa 90% da largura da tela */
  height: calc(100vh - (2 * ${CARD_VERTICAL_PAGE_MARGIN})); /* <--- ALTERADO AQUI: Altura baseada na nova margem vertical */
  max-width: var(--container-lg); /* Mantido para telas muito grandes */
  margin: ${CARD_VERTICAL_PAGE_MARGIN} auto; /* <--- ALTERADO AQUI: Margem vertical e centralização horizontal */
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--gray-200);
  background-color: white;
  border-radius: var(--radius);
  overflow: hidden; /* Importante para o iframe */
  display: flex;
  flex-direction: column;
`;

const HeaderContainer = styled.div`
  padding: 1.5rem;
  display: flex;
  justify-content: center;
  border-bottom: 1px solid var(--gray-200);
  background-color: white;
`;

const IframeContentWrapper = styled.div`
  flex: 1; /* Ocupa todo o espaço vertical restante */
  width: 100%; /* Ocupa 100% da largura do pai (CardContainerDiv) */
  height: 100%; /* Ocupa 100% da altura disponível (flex:1) dentro do CardContainerDiv */
`;


/* ---------------- Component ---------------- */
export default function AgrRoom() {
    const navigate = useNavigate();
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const hasFetchedToken = useRef(false);
    const errorToastId = useRef<string | number | null>(null);

    const showToast = (type: 'success' | 'error' | 'info' | 'warning', message: string, toastIdKey: string = 'agr-default') => {
        showManagedToast(type, message, toastIdKey);
    };

    useEffect(() => {
        if (hasFetchedToken.current) {
            return;
        }
        hasFetchedToken.current = true;

        const operatorData = sessionStorage.getItem("operator");
        if (!operatorData) {
            showToast("error", "Sessão do operador não encontrada. Por favor, faça login novamente.", "agr-session-error");
            navigate("/", { replace: true });
            setIsLoading(false);
            return;
        }

        try {
            const operator = JSON.parse(operatorData);
            if (!operator?.cpf || !operator?.name || !operator?.certificateOffices) {
                showToast("error", "Dados do operador estão incompletos.", "agr-operator-data-error");
                navigate("/", { replace: true });
                setIsLoading(false);
                return;
            }

            getNuvidioToken(operator)
                .then((tk) => {
                    if (!tk) {
                        throw new Error("Token de autenticação para a sala não foi retornado.");
                    }
                    setToken(tk);
                })
                .catch((err: Error) => {
                    console.error("[AGR] Erro ao obter token do NuVidio:", err);
                    const msg = `Falha ao carregar a sala de atendimento: ${err.message}`;
                    setError(msg);
                    showToast("error", msg, "agr-token-fetch-error");
                    setIsLoading(false);
                });
        } catch (err) {
            console.error("[AGR] Erro ao processar dados do operador:", err);
            const msg = "Ocorreu um erro ao iniciar a sessão.";
            showToast("error", msg, "agr-init-error");
            navigate("/", { replace: true });
            setIsLoading(false);
        }
    }, [navigate]);

    const iframeURL = token
        ? `${VITE_NUVIDIO}?ready=false&confirmation=false&sidebar=false&token=${token}`
        : "";

    const handleIframeLoad = () => setIsLoading(false);

    useEffect(() => {
        if (error) {
            setIsLoading(false);
        }
    }, [error]);

    const handleLogout = () => {
        sessionStorage.removeItem("operator");
        showToast("info", "Você foi desconectado.", "agr-logout-info");
        navigate("/", { replace: true });
    };

    return (
        <PageWrapper>
            {isLoading && <Loading message="Carregando sala de atendimento..." />}

            {error && !isLoading && <ErrorMessage>{error}</ErrorMessage>}

            {!error && (
                <>
                    <LogoutButton onClick={handleLogout} disabled={isLoading}>
                        <ArrowLeft className="h-5 w-5" />
                    </LogoutButton>

                    <CardContainerDiv style={{ visibility: isLoading ? "hidden" : "visible" }}>
                        <HeaderContainer>
                            <LogoImage src="/logo-vidas.png" alt="Vidas" />
                        </HeaderContainer>

                        <IframeContentWrapper>
                            {token && (
                                <NuvidioIframe
                                    id="iframeCenter"
                                    allow="camera; microphone; fullscreen; display-capture"
                                    allowFullScreen
                                    src={iframeURL}
                                    title="Sala de Atendimento NuVidio"
                                    onLoad={handleIframeLoad}
                                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
                                />
                            )}
                        </IframeContentWrapper>
                    </CardContainerDiv>
                </>
            )}
        </PageWrapper>
    );
}